import React, { useEffect, useState } from "react";
import { Card, Form, Button, Row, Col, Image, Alert } from '@themesberg/react-bootstrap';
import { useHistory, useParams } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import { useAuth } from "../../context/AuthContext";
import { Routes } from "../../routes";

export default function MyProfileForm() {
    const history = useHistory();
    const { currentUser, refreshCurrentUser } = useAuth();
    const { id } = useParams();
    const isEdit = id && id !== 'new';

    // State for each field
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [gender, setGender] = useState("0");
    const [status, setStatus] = useState("1");
    const [phone, setPhone] = useState("");
    const [martial_status, setMaritalStatus] = useState("");
    const [education_level, setEducationLevel] = useState("");
    const [location, setLocation] = useState("");
    const [image, setImage] = useState(null);
    const [existingUri, setExistingUri] = useState("");
    const [saving, setSaving] = useState(false);

    const [errors, setErrors] = useState({});
    const [submitError, setSubmitError] = useState("");

    // Load user data if editing
    useEffect(() => {
        // Always load current user's profile for this page (MyProfile)
        if (!currentUser || !currentUser.id) return;
        api.get(`/edit-users/${currentUser.id}`).then(({ data }) => {
            const u = data.data || {};
            setName(u.name || "");
            setEmail(u.email || "");
            setGender(u.gender ? String(u.gender) : "0");
            setPhone(u.phone || "");
            setMaritalStatus(u.martial_status || "");
            setEducationLevel(u.education_level || "");
            setLocation(u.location || "");
            setExistingUri(u.uri || "");
        }).catch(() => {});
    }, [currentUser && currentUser.id]);

    // Validation function
    const validate = () => {
        const newErrors = {};
        if (!name || name.trim() === "") newErrors.name = "Name is required";
        if (!email || email.trim() === "") newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = "Invalid email";
        if (!gender || gender === "0") newErrors.gender = "Gender is required";
        if (!phone || phone.trim() === "") newErrors.phone = "Phone is required";
        else if (!/^\+?[0-9]{7,15}$/.test(phone)) newErrors.phone = "Invalid phone number";
        if (!martial_status || martial_status.trim() === "") newErrors.martial_status = "Martial status is required";
        if (!education_level || education_level.trim() === "") newErrors.education_level = "Education level is required";
        if (!location || location.trim() === "") newErrors.location = "Location is required";
        return newErrors;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        const validationErrors = validate();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
            setSubmitError("Please fix the errors above.");
            return;
        }
        setSaving(true);
        try {
            let res = null;
            if (image instanceof File) {
                const fd = new FormData();
                fd.append('name', name);
                fd.append('email', email);
                fd.append('gender', gender);
                fd.append('status', status);
                fd.append('phone', phone);
                fd.append('martial_status', martial_status);
                fd.append('admin_images', image);
                fd.append('education_level', education_level);
                fd.append('location', location);
                if (isEdit) res = await api.put(`/updateprofile/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                else res = await api.post(`/updateprofile`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else {
                const payload = { name, email, gender, status, phone, martial_status, education_level, location };
                if (isEdit) res = await api.put(`/updateprofile/${id}`, payload);
                else res = await api.post(`/updateprofile`, payload);
            }

            // If server returned updated user, update local state so UI shows changes immediately
            const updated = res?.data?.data;
            if (updated) {
                setName(updated.name || name);
                setEmail(updated.email || email);
                setGender(updated.gender ? String(updated.gender) : gender);
                setPhone(updated.phone || phone);
                setMaritalStatus(updated.martial_status || martial_status);
                setEducationLevel(updated.education_level || education_level);
                setLocation(updated.location || location);
                // update existingUri (virtual) if provided
                if (updated.uri) setExistingUri(updated.uri);
                // clear local file input state so preview uses existingUri
                setImage(null);
                // update context so Navbar and other components reflect the new user
                if (typeof refreshCurrentUser === 'function') {
                    try { await refreshCurrentUser(); } catch (e) { /* ignore */ }
                } else {
                    // fallback: update localStorage user
                    const stored = JSON.parse(localStorage.getItem('user') || '{}');
                    localStorage.setItem('user', JSON.stringify({ ...stored, ...updated }));
                }
            }

            await Swal.fire({ title: 'Success', text: `Profile ${isEdit ? 'updated' : 'created'} successfully`, icon: 'success', timer: 1500, showConfirmButton: false, heightAuto: false });
            // keep user on the same page and reflect updated data
        } catch (e) {
            await Swal.fire({ title: 'Error', text: e?.response?.data?.message || e.message || 'Failed to save', icon: 'error', heightAuto: false });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <h5 className="mb-0">{isEdit ? 'Edit' : 'Create'} Profile</h5>
            </Card.Header>
            <Card.Body>
                <Form onSubmit={onSubmit} noValidate>
                    {submitError && <Alert variant="danger">{submitError}</Alert>}
                    <Row>
                        <Col md={8}>
                            <Form.Group className="mb-3">
                                <Form.Label>Name</Form.Label>
                                <Form.Control
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    isInvalid={!!errors.name}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    isInvalid={!!errors.email}
                                    required
                                />
                                <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Gender</Form.Label>
                                <Form.Select
                                    value={gender}
                                    onChange={(e) => setGender(e.target.value)}
                                    isInvalid={!!errors.gender}
                                >
                                    <option value="0">Select Gender</option>
                                    <option value="2">Male</option>
                                    <option value="1">Female</option>
                                    <option value="3">Other</option>
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">{errors.gender}</Form.Control.Feedback>
                            </Form.Group>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Marital Status</Form.Label>
                                        <Form.Select 
                                        value={martial_status}
                                        onChange={(e) => setMaritalStatus(e.target.value)}
                                        isInvalid={!!errors.martial_status}
                                        >
                                            <option value="">Select Marital Status</option>
                                            <option value="1">Single</option>
                                            <option value="2">Married</option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{errors.martial_status}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group id="phone">
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control 
                                        value={phone} 
                                        onChange={(e) => setPhone(e.target.value)}
                                        isInvalid={!!errors.phone}
                                         />
                                    </Form.Group>
                                    <Form.Control.Feedback type="invalid">{errors.phone}</Form.Control.Feedback>
                                </Col>
                                <Col md={6}>
                                    <Form.Group id="location">
                                        <Form.Label>Location</Form.Label>
                                        <Form.Control 
                                        value={location} 
                                        onChange={(e) => setLocation(e.target.value)}
                                        isInvalid={!!errors.location}
                                         />
                                    </Form.Group>
                                    <Form.Control.Feedback type="invalid">{errors.location}</Form.Control.Feedback>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Education Level</Form.Label>
                                        <Form.Select 
                                        value={education_level} 
                                        onChange={(e) => setEducationLevel(e.target.value)}
                                        isInvalid={!!errors.education_level}
                                        >
                                            <option value="">Education Level</option>
                                            <option value="1">High School</option>
                                            <option value="2">Graduation</option>
                                            <option value="3">Postgraduate</option>
                                            <option value="4">Doctorate</option>
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">{errors.education_level}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label>Image</Form.Label>
                                <div className="mb-2">
                                    {image instanceof File ? (
                                        <Image src={URL.createObjectURL(image)} thumbnail />
                                    ) : existingUri ? (
                                        <Image src={existingUri} thumbnail />
                                    ) : null}
                                </div>
                                <Form.Control type="file" accept="image/*" onChange={(e) => setImage(e.target.files[0])} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end">
                        <Button variant="secondary" className="me-2" onClick={() => history.push(Routes.DashboardOverview.path)}>Cancel</Button>
                        <Button type="submit" variant="primary" disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button>
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
}